import React, { useEffect, useState } from 'react';
import { Modal, Table, Tag, Empty, Spin, Collapse } from 'antd';
import { Consignment, ConsignmentPeriod, ConsignmentSettlement } from '../../model/consignment';
import { api_consignment } from '../../api/consignment';
import { formatEventTime } from '../../utils/audit-format';

interface Props {
    open: boolean;
    item: Consignment | null;
    onClose: () => void;
}

const sourceTypeLabel = (sourceType?: string) => {
    switch (sourceType) {
        case 'INITIAL': return 'ฝากเริ่มต้น';
        case 'ADD_STOCK': return 'ฝากเพิ่ม';
        case 'CARRY_FORWARD': return 'ยกยอดจากรอบก่อน';
        default: return sourceType || '-';
    }
};

/**
 * Read-only Period history. Every Period (OPEN and CLOSED) belongs to the
 * Consignment header, not to any one Product — every Batch shown under a
 * Period may span multiple Products. Closed Periods are display-only; no
 * edit affordance is offered here or anywhere else in the Website.
 */
const money = (value: number) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const ConsignmentPeriodHistoryModal: React.FC<Props> = ({ open, item, onClose }) => {
    const [periods, setPeriods] = useState<ConsignmentPeriod[]>([]);
    const [settlements, setSettlements] = useState<ConsignmentSettlement[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && item) {
            loadPeriods(item.id);
        }
    }, [open, item]);

    const loadPeriods = async (id: number) => {
        setLoading(true);
        try {
            const [periodsRes, settlementsRes] = await Promise.all([
                api_consignment('PERIODS', {}, id),
                api_consignment('SETTLEMENTS', {}, id)
            ]);
            setPeriods(Array.isArray(periodsRes) ? periodsRes : []);
            setSettlements(Array.isArray(settlementsRes) ? settlementsRes : []);
        } catch (e) {
            console.error('Failed to fetch period history', e);
        } finally {
            setLoading(false);
        }
    };

    if (!item) return null;

    // Structured, three-tier header: Period title (strongest) + status Tag
    // on the primary row, open/close timestamps (secondary) on the row
    // below -- wraps cleanly on narrower widths instead of the previous
    // single-line text run that squeezed title/status/timestamps together.
    const renderPeriodHeader = (period: ConsignmentPeriod) => (
        <div className="consignment-period-header">
            <div className="consignment-period-header-top">
                <span className="consignment-period-title">รอบบิลที่ {period.period_no}</span>
                {period.status === 'OPEN'
                    ? <Tag color="green">เปิดอยู่</Tag>
                    : <Tag>ปิดแล้ว</Tag>}
            </div>
            {/* Phase 7C-6C: Backend sends these as
                DATE_FORMAT(..., '%Y-%m-%d %H:%i:%S') -- Bangkok wall clock
                with no offset, and with no `_iso` counterpart of the kind
                7C-6B added for movement history. `formatEventTime` reads
                that shape literally rather than letting the engine guess a
                timezone, so the value is unchanged and only its rendering
                now matches the movement history. The missing canonical
                `+07:00` field is a Backend gap, reported not papered over. */}
            <div className="consignment-period-meta">
                <span className="consignment-period-meta-item" data-testid="period-opened-at">เปิดเมื่อ {formatEventTime(period.opened_at)}</span>
                {period.closed_at && (
                    <span className="consignment-period-meta-item" data-testid="period-closed-at">ปิดเมื่อ {formatEventTime(period.closed_at)}</span>
                )}
            </div>
        </div>
    );

    const batchColumns = [
        {
            title: 'สินค้า',
            key: 'product',
            render: (_: any, r: any) => (
                <div>
                    <span style={{ fontWeight: 600 }}>{r.product_name}</span>
                    {r.product_code && <span style={{ marginLeft: 6, fontSize: 11, color: '#64748B' }}>({r.product_code})</span>}
                </div>
            )
        },
        { title: 'Batch', dataIndex: 'batch_no', key: 'batch_no', width: 70, render: (v: number) => <Tag>#{v}</Tag> },
        { title: 'ที่มา', dataIndex: 'source_type', key: 'source_type', width: 110, render: sourceTypeLabel },
        { title: 'รับ', dataIndex: 'quantity_received', key: 'quantity_received', width: 70, align: 'right' as const },
        { title: 'คงเหลือ', dataIndex: 'latest_actual_remaining', key: 'latest_actual_remaining', width: 80, align: 'right' as const },
        { title: 'ขายได้', dataIndex: 'quantity_sold', key: 'quantity_sold', width: 70, align: 'right' as const },
        { title: 'จ่ายแล้ว', dataIndex: 'quantity_settled', key: 'quantity_settled', width: 75, align: 'right' as const },
        {
            title: 'ยกไปงวดถัดไป',
            dataIndex: 'quantity_carried_forward',
            key: 'quantity_carried_forward',
            width: 100,
            align: 'right' as const,
            render: (v: number) => Number(v || 0) > 0 ? <strong className="consignment-period-carry-value">{v}</strong> : 0
        },
        {
            title: 'ต้นทุนผู้ฝาก',
            dataIndex: 'owner_cost_per_unit',
            key: 'owner_cost_per_unit',
            width: 100,
            align: 'right' as const,
            render: (v: number) => `฿${Number(v || 0).toFixed(2)}`
        }
    ];

    return (
        <Modal
            className="consignment-period-history-modal"
            open={open}
            title={`🗂️ ประวัติรอบบิล: ${item.document_no}`}
            onCancel={onClose}
            footer={null}
            width={1050}
            destroyOnClose
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin tip="กำลังโหลดประวัติรอบบิล..." />
                </div>
            ) : periods.length === 0 ? (
                <Empty description="ยังไม่มีข้อมูลรอบบิล" />
            ) : (
                <Collapse
                    className="consignment-period-collapse"
                    defaultActiveKey={periods.filter(p => p.status === 'OPEN').map(p => String(p.id))}
                    items={[...periods].reverse().map(period => ({
                        key: String(period.id),
                        label: renderPeriodHeader(period),
                        children: (
                            <>
                                <div className="consignment-period-table-wrap">
                                    <Table
                                        columns={batchColumns}
                                        dataSource={(period.batches || []).map((b, i) => ({ ...b, key: b.id || i }))}
                                        pagination={false}
                                        size="small"
                                        bordered
                                        scroll={{ x: 'max-content' }}
                                        locale={{ emptyText: 'ไม่มี Batch ในรอบบิลนี้' }}
                                    />
                                </div>
                                {settlements.filter(s => s.period_id === period.id).length > 0 && (
                                    <div className="consignment-period-settlements">
                                        <div className="consignment-period-settlements-title">การจ่ายเงินในรอบบิลนี้</div>
                                        {settlements.filter(s => s.period_id === period.id).map(s => (
                                            <div key={s.id} className="consignment-period-settlement-card">
                                                <span>เลขที่ {s.settlement_no} · {s.settlement_date}</span>
                                                <span>รวม {s.total_quantity} ชิ้น · ฿{money(s.total_amount)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )
                    }))}
                />
            )}
        </Modal>
    );
};

export default ConsignmentPeriodHistoryModal;
